import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  UsersIcon,
  UserPlusIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { UserGroupIcon as UserGroupIconSolid } from '@heroicons/react/24/solid';
import logger from '../../utils/logger';

const AddMembersModal = ({ open, onClose, team, onSubmit, existingMembers, showNotification }) => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedRole, setSelectedRole] = useState('member');

  const fetchUsers = useCallback(async (newSearch = false) => {
    if (newSearch) {
      setPage(1);
      setUsers([]);
    }

    setLoading(true);
    try {
      const response = await axiosInstance.get("/api/business/users", {
        params: {
          page: newSearch ? 1 : page,
          limit: 10,
          search: searchQuery,
          status: "active"
        }
      });

      if (response.data.success) {
        const newUsers = response.data.users;

        if (newSearch) {
          setUsers(newUsers);
        } else {
          setUsers(prev => {
            const existingIds = new Set(prev.map(u => u.id));
            const uniqueNewUsers = newUsers.filter(user => !existingIds.has(user.id));
            return [...prev, ...uniqueNewUsers];
          });
        }
        setHasMore(response.data.pagination?.page < response.data.pagination?.pages);
      }
    } catch (error) {
      logger.error('Error fetching users:', error);
      showNotification?.('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery]);

  useEffect(() => {
    if (open) {
      setUsers([]);
      setPage(1);
      fetchUsers(true);
    }
  }, [open, fetchUsers]);

  useEffect(() => {
    if (open && page > 1) {
      fetchUsers(false);
    }
  }, [page, open, fetchUsers]);

  const filteredUsers = users.filter(user => 
    !existingMembers?.some(member => member.userId === user.id)
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchUsers(true);
  };

  const handleUserSelect = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u.id === user.id);
      if (isSelected) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, user];
      }
    });
  };

  const handleSubmit = async () => {
    try {
      const membersToAdd = selectedUsers.map(user => ({
        userId: user.id,
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        role: selectedRole,
        status: 'active'
      }));

      const response = await axiosInstance.post(
        `/api/business/teams/${team.id}/members`,
        { members: membersToAdd }
      );

      if (response.data.success) {
        showNotification?.('Members added successfully', 'success');

        if (onSubmit) {
          await onSubmit(membersToAdd);
        }

        onClose();
        setSelectedUsers([]);
        setSearchQuery('');
      }
    } catch (error) {
      logger.error('Error adding members:', error);
      showNotification?.(error.response?.data?.message || 'Failed to add members', 'error');
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div
        className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Clean and Sleek */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 dark:bg-[#111111]">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <UserPlusIcon className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add Members</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{team?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-lg p-2 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Search and Role Selection */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-[#222222] border-b border-gray-200 dark:border-gray-800">
          <div className="flex gap-3">
            <div className="flex-1">
              <form onSubmit={handleSearchSubmit} className="relative">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name, email, or department..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-10 py-2.5 border border-gray-300 dark:border-gray-700 dark:bg-[#2a2a2a] dark:text-white dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                  />
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery('');
                        fetchUsers(true);
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="w-40">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-700 dark:bg-[#2a2a2a] dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>

          {/* Selection summary */}
          {selectedUsers.length > 0 && (
            <div className="mt-3 flex items-center justify-between bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg px-4 py-2">
              <div className="flex items-center space-x-2">
                <CheckCircleIcon className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-medium text-orange-900 dark:text-orange-200">
                  {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                </span>
              </div>
              <button
                onClick={() => setSelectedUsers([])}
                className="text-xs text-orange-600 hover:text-orange-700 font-medium"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* User list */}
        <div className="flex-1 overflow-y-auto">
          {filteredUsers.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <UsersIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 font-medium">
                {searchQuery ? 'No users found matching your search' : 'No available users to add'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                {searchQuery ? 'Try a different search term' : 'All users are already members'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-800">
              {filteredUsers.map(user => {
                const isSelected = selectedUsers.some(u => u.id === user.id);
                return (
                  <label
                    key={user.id}
                    className={`flex items-center px-6 py-4 cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30'
                        : 'hover:bg-gray-50 dark:hover:bg-[#222222]'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleUserSelect(user)}
                      className="w-4 h-4 text-orange-600 border-gray-300 dark:border-gray-600 dark:bg-[#2a2a2a] rounded focus:ring-orange-500 transition-all"
                    />

                    <div className="ml-4 flex items-center flex-1 min-w-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {getInitials(user.firstName, user.lastName)}
                      </div>

                      <div className="ml-4 flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {user.firstName} {user.lastName}
                          </p>
                          {isSelected && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-orange-600 text-white font-medium">
                              Selected
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate mt-0.5">
                          {user.email}
                        </p>
                      </div>

                      {user.department && (
                        <span className="ml-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full">
                          {user.department}
                        </span>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {loading && (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 dark:border-gray-700 border-t-orange-500"></div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">Loading users...</p>
            </div>
          )}

          {hasMore && !loading && filteredUsers.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800">
              <button
                onClick={() => setPage(prev => prev + 1)}
                className="w-full py-2.5 text-sm text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 font-medium bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 rounded-lg transition-colors"
              >
                Load More Users
              </button>
            </div>
          )}
        </div>

        {/* Footer - Clean and Sleek */}
        <div className="flex items-center justify-between px-6 py-3.5 bg-white dark:bg-[#111111] border-t border-gray-200 dark:border-gray-800">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {selectedUsers.length > 0 && (
              <span className="flex items-center space-x-1.5">
                <span className="inline-flex items-center justify-center w-5 h-5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-full text-xs font-semibold">
                  {selectedUsers.length}
                </span>
                <span>selected as {selectedRole}</span>
              </span>
            )}
          </div>

          <div className="flex space-x-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#2a2a2a] border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={selectedUsers.length === 0}
              className={`flex items-center space-x-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                selectedUsers.length === 0
                  ? 'bg-gray-200 dark:bg-[#222222] text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-orange-600 text-white hover:bg-orange-700'
              }`}
            >
              <UserPlusIcon className="w-4 h-4" />
              <span>Add Members</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMembersModal;